import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { toast } from 'sonner';
import { Users, ChevronLeft, ChevronRight, Save, Filter } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

const GenderReview = () => {
  const [adolescents, setAdolescents] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [genderFilter, setGenderFilter] = useState('all');
  const [engagementFilter, setEngagementFilter] = useState('ativo');
  const [currentPage, setCurrentPage] = useState(0);
  const [pendingChanges, setPendingChanges] = useState({});
  const [saving, setSaving] = useState(false);
  
  const itemsPerPage = 20;

  useEffect(() => {
    fetchAdolescents();
  }, [genderFilter, engagementFilter]);

  const fetchAdolescents = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/adolescents/gender-review`, {
        params: {
          gender_filter: genderFilter,
          engagement_filter: engagementFilter
        }
      });
      setAdolescents(response.data.adolescents || []);
      setStats(response.data.stats || null);
      setCurrentPage(0);
    } catch (error) {
      toast.error('Erro ao carregar adolescentes');
    } finally {
      setLoading(false);
    }
  };

  // Adolescents já vêm filtrados do backend
  const filteredAdolescents = adolescents;

  const handleGenderChange = (id, newGender) => {
    setPendingChanges(prev => ({
      ...prev,
      [id]: newGender
    }));
  };

  const handleSaveChanges = async () => {
    const updates = Object.entries(pendingChanges).map(([id, gender]) => ({
      id,
      gender
    }));

    if (updates.length === 0) {
      toast.info('Nenhuma alteração para salvar');
      return;
    }

    setSaving(true);
    try {
      const response = await axios.post(`${API}/adolescents/bulk-update-gender`, updates);
      toast.success(`${response.data.updated} adolescentes atualizados!`);
      setPendingChanges({});
      fetchAdolescents();
    } catch (error) {
      toast.error('Erro ao salvar alterações');
    } finally {
      setSaving(false);
    }
  };

  const totalPages = Math.ceil(filteredAdolescents.length / itemsPerPage);
  const currentItems = filteredAdolescents.slice(
    currentPage * itemsPerPage,
    (currentPage + 1) * itemsPerPage
  );

  const getGenderDisplay = (id, originalGender) => {
    return pendingChanges[id] || originalGender;
  };

  const hasChanges = Object.keys(pendingChanges).length > 0;

  return (
    <div className="fade-in">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Revisão de Gênero</h1>
          <p className="text-gray-500 mt-1">Corrija o gênero dos adolescentes cadastrados</p>
        </div>
        
        {hasChanges && (
          <Button 
            onClick={handleSaveChanges} 
            disabled={saving}
            className="btn-primary flex items-center space-x-2"
          >
            <Save size={20} />
            <span>{saving ? 'Salvando...' : `Salvar ${Object.keys(pendingChanges).length} alterações`}</span>
          </Button>
        )}
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3 mb-6">
          <div className="card p-3 text-center bg-gradient-to-br from-gray-50 to-gray-100">
            <p className="text-2xl font-bold text-gray-800">{stats.total}</p>
            <p className="text-xs text-gray-500">Total</p>
          </div>
          <div className="card p-3 text-center bg-gradient-to-br from-blue-50 to-blue-100">
            <p className="text-2xl font-bold text-blue-800">{stats.masculino}</p>
            <p className="text-xs text-blue-600">👦 Meninos</p>
          </div>
          <div className="card p-3 text-center bg-gradient-to-br from-pink-50 to-pink-100">
            <p className="text-2xl font-bold text-pink-800">{stats.feminino}</p>
            <p className="text-xs text-pink-600">👧 Meninas</p>
          </div>
          <div className="card p-3 text-center bg-gradient-to-br from-amber-50 to-amber-100 border-2 border-amber-300">
            <p className="text-2xl font-bold text-amber-800">{stats.outro}</p>
            <p className="text-xs text-amber-600">⚠️ A Corrigir</p>
          </div>
          <div className="card p-3 text-center bg-gradient-to-br from-green-50 to-green-100">
            <p className="text-2xl font-bold text-green-800">{stats.ativos}</p>
            <p className="text-xs text-green-600">✓ Ativos</p>
          </div>
          <div className="card p-3 text-center bg-gradient-to-br from-orange-50 to-orange-100">
            <p className="text-2xl font-bold text-orange-800">{stats.desengajados}</p>
            <p className="text-xs text-orange-600">⚠ Desengaj.</p>
          </div>
          <div className="card p-3 text-center bg-gradient-to-br from-red-50 to-red-100">
            <p className="text-2xl font-bold text-red-800">{stats.inativos}</p>
            <p className="text-xs text-red-600">✗ Inativos</p>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="card mb-6">
        <div className="flex flex-wrap items-center gap-4">
          <Filter size={20} className="text-gray-400" />
          <span className="text-gray-600">Filtrar:</span>
          
          <Select value={engagementFilter} onValueChange={setEngagementFilter}>
            <SelectTrigger className="w-44">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">📋 Todos Engajamentos</SelectItem>
              <SelectItem value="ativo">✓ Apenas Ativos</SelectItem>
              <SelectItem value="desengajado">⚠ Apenas Desengajados</SelectItem>
              <SelectItem value="inativo">✗ Apenas Inativos</SelectItem>
            </SelectContent>
          </Select>

          <Select value={genderFilter} onValueChange={setGenderFilter}>
            <SelectTrigger className="w-44">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">📋 Todos Gêneros</SelectItem>
              <SelectItem value="masculino">👦 Meninos</SelectItem>
              <SelectItem value="feminino">👧 Meninas</SelectItem>
              <SelectItem value="outro">⚠️ A Corrigir</SelectItem>
            </SelectContent>
          </Select>

          <span className="text-gray-500 text-sm ml-auto">
            {adolescents.length} adolescentes encontrados
          </span>
        </div>
      </div>

      {/* Instructions */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
        <h3 className="font-bold text-blue-800 mb-2">Como corrigir:</h3>
        <ol className="text-sm text-blue-700 list-decimal list-inside space-y-1">
          <li>Clique em <strong>"👦 Menino"</strong> ou <strong>"👧 Menina"</strong> para cada adolescente</li>
          <li>As alterações ficam marcadas em <span className="bg-yellow-200 px-1">amarelo</span></li>
          <li>Quando terminar, clique no botão <strong>"Salvar alterações"</strong></li>
        </ol>
      </div>

      {/* Table */}
      {loading ? (
        <div className="text-center py-12">Carregando...</div>
      ) : filteredAdolescents.length === 0 ? (
        <div className="card text-center py-12">
          <Users size={64} className="mx-auto mb-4 text-gray-300" />
          <p className="text-gray-500 text-lg">Nenhum adolescente encontrado com este filtro</p>
        </div>
      ) : (
        <>
          <div className="card overflow-hidden">
            <table className="table w-full">
              <thead>
                <tr>
                  <th className="w-12">#</th>
                  <th>Nome</th>
                  <th>Engajamento</th>
                  <th>Presenças</th>
                  <th>Gênero Atual</th>
                  <th>Corrigir Para</th>
                </tr>
              </thead>
              <tbody>
                {currentItems.map((adolescent, index) => {
                  const displayGender = getGenderDisplay(adolescent.id, adolescent.gender);
                  const hasChange = pendingChanges[adolescent.id];
                  
                  return (
                    <tr key={adolescent.id} className={hasChange ? 'bg-yellow-50' : ''}>
                      <td className="text-gray-500">
                        {currentPage * itemsPerPage + index + 1}
                      </td>
                      <td className="font-semibold">{adolescent.name}</td>
                      <td>
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                          adolescent.engagement_status === 'ativo' ? 'bg-green-100 text-green-800' :
                          adolescent.engagement_status === 'desengajado' ? 'bg-amber-100 text-amber-800' :
                          'bg-red-100 text-red-800'
                        }`}>
                          {adolescent.engagement_status === 'ativo' ? 'Ativo' :
                           adolescent.engagement_status === 'desengajado' ? 'Desengajado' : 'Inativo'}
                        </span>
                      </td>
                      <td className="text-sm text-gray-600">
                        {adolescent.total_checkins || 0} ({adolescent.attendance_rate || 0}%)
                      </td>
                      <td>
                        <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${
                          displayGender === 'masculino' 
                            ? 'bg-blue-100 text-blue-800' 
                            : displayGender === 'feminino'
                            ? 'bg-pink-100 text-pink-800'
                            : 'bg-amber-100 text-amber-800'
                        }`}>
                          {displayGender === 'masculino' ? '👦 Masculino' : 
                           displayGender === 'feminino' ? '👧 Feminino' : '⚠️ Outro'}
                        </span>
                        {hasChange && (
                          <span className="ml-2 text-xs text-amber-600">(alterado)</span>
                        )}
                      </td>
                      <td>
                        <div className="flex space-x-2">
                          <button
                            onClick={() => handleGenderChange(adolescent.id, 'masculino')}
                            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition ${
                              displayGender === 'masculino'
                                ? 'bg-blue-500 text-white'
                                : 'bg-blue-50 text-blue-700 hover:bg-blue-100'
                            }`}
                          >
                            👦 Menino
                          </button>
                          <button
                            onClick={() => handleGenderChange(adolescent.id, 'feminino')}
                            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition ${
                              displayGender === 'feminino'
                                ? 'bg-pink-500 text-white'
                                : 'bg-pink-50 text-pink-700 hover:bg-pink-100'
                            }`}
                          >
                            👧 Menina
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-4">
              <p className="text-sm text-gray-500">
                Página {currentPage + 1} de {totalPages}
              </p>
              <div className="flex space-x-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(p => Math.max(0, p - 1))}
                  disabled={currentPage === 0}
                >
                  <ChevronLeft size={16} />
                  Anterior
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(p => Math.min(totalPages - 1, p + 1))}
                  disabled={currentPage >= totalPages - 1}
                >
                  Próxima
                  <ChevronRight size={16} />
                </Button>
              </div>
            </div>
          )}

          {/* Save button at bottom too */}
          {hasChanges && (
            <div className="mt-6 flex justify-center">
              <Button 
                onClick={handleSaveChanges} 
                disabled={saving}
                className="btn-primary flex items-center space-x-2"
                size="lg"
              >
                <Save size={20} />
                <span>{saving ? 'Salvando...' : `Salvar ${Object.keys(pendingChanges).length} alterações`}</span>
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default GenderReview;
